import React, { useState, useEffect, useMemo } from 'react';
import { useAuthenticator } from '@aws-amplify/ui-react';
import { 
  Container, 
  Row, 
  Col, 
  Card, 
  Button, 
  Spinner, 
  Alert, 
  Form
} from 'react-bootstrap';
import { useHistory } from 'react-router-dom';
import moment from 'moment';
import CustomPagination from '../components/CustomPagination';
import PortalPageLayout from '../components/PortalPageLayout';
import { 
  useGetNotificationsQuery, 
  useUpdateNotificationMutation,
  NotificationAction 
} from '../store/api/notificationApi';

const UserNotifications = () => {
  const { user } = useAuthenticator((context) => [context.user]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10
  });
  const [filters] = useState({
    status: '', // 'read', 'unread', or ''
    type: '', // specific notification type or ''
    sortBy: 'created_at',
    sortOrder: 'DESC'
  });
  const [selectedNotifications, setSelectedNotifications] = useState(new Set());
  const [readingActions, setReadingActions] = useState(new Set()); // Track "mark as read" actions
  const [unreadingActions, setUnreadingActions] = useState(new Set()); // Track "mark as unread" actions
  const [bulkLoading, setBulkLoading] = useState(false); // Track bulk action loading
  const [error, setError] = useState(null);
  const history = useHistory();

  // RTK Query hooks with enhanced parameters
  const { 
    data: notificationResponse, 
    isLoading, 
    error: notificationsError,
    isFetching
  } = useGetNotificationsQuery({
    page: pagination.page,
    limit: pagination.limit,
    status: filters.status || undefined,
    type: filters.type || undefined,
    sortBy: filters.sortBy,
    sortOrder: filters.sortOrder,
    includeStats: true,
  });

  const [updateNotification] = useUpdateNotificationMutation();

  // Extract data from API response
  const notifications = useMemo(() => notificationResponse?.notifications || [], [notificationResponse]);
  const paginationData = notificationResponse?.pagination || {};
  const stats = notificationResponse?.stats || {};

  // Handle RTK Query errors
  useEffect(() => {
    if (notificationsError) {
      setError(notificationsError.data?.message || 'Error loading notifications. Please try again later.');
    } else {
      setError(null);
    }
  }, [notificationsError]);

  // Reset to first page when filters change
  useEffect(() => {
    setPagination(prev => ({ ...prev, page: 1 }));
    setSelectedNotifications(new Set());
  }, [filters.status, filters.type, filters.sortBy, filters.sortOrder]);

  const handleMarkAsRead = async (notificationId) => {
    try {
      setReadingActions(prev => new Set(prev).add(notificationId));
      
      await updateNotification({
        id: notificationId,
        action: NotificationAction.MARK_READ
      }).unwrap();
      
      // Clear any existing errors
      setError(null);
    } catch (err) {
      console.error('Error marking notification as read:', err);
      setError(err.data?.message || 'Failed to mark notification as read');
    } finally {
      setReadingActions(prev => {
        const newSet = new Set(prev);
        newSet.delete(notificationId);
        return newSet;
      });
    }
  };

  const handleMarkAsUnread = async (notificationId) => {
    try {
      setUnreadingActions(prev => new Set(prev).add(notificationId));
      
      await updateNotification({
        id: notificationId,
        action: NotificationAction.MARK_UNREAD
      }).unwrap();
      
      // Clear any existing errors
      setError(null);
    } catch (err) {
      console.error('Error marking notification as unread:', err);
      setError(err.data?.message || 'Failed to mark notification as unread');
    } finally {
      setUnreadingActions(prev => {
        const newSet = new Set(prev);
        newSet.delete(notificationId);
        return newSet;
      });
    }
  };

  const handleMarkMultipleAsRead = async () => {
    if (selectedNotifications.size === 0) return;
    
    try {
      setBulkLoading(true);

      // Process each notification individually since there's no bulk endpoint
      const promises = Array.from(selectedNotifications).map(id => 
        updateNotification({
          id: id,
          action: NotificationAction.MARK_READ
        }).unwrap()
      );
      
      await Promise.all(promises);
      
      // Clear selections after successful bulk update
      setSelectedNotifications(new Set());
      setError(null);
    } catch (err) {
      console.error('Error marking notifications as read:', err);
      setError(err.data?.message || 'Failed to mark some notifications as read');
    } finally {
      setBulkLoading(false);
    }
  };

  const handleSelectNotification = (notificationId, checked) => {
    setSelectedNotifications(prev => {
      const newSet = new Set(prev);
      if (checked) {
        newSet.add(notificationId);
      } else {
        newSet.delete(notificationId);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    const unreadNotifications = notifications.filter(n => n.status === 'unread');
    if (selectedNotifications.size === unreadNotifications.length && unreadNotifications.length > 0) {
      setSelectedNotifications(new Set());
    } else {
      setSelectedNotifications(new Set(unreadNotifications.map(n => n.id)));
    }
  };

  const formatDate = (dateString) => {
    return moment(dateString).format('MMM DD, YYYY h:mm A');
  };

  const handlePageChange = (page) => {
    setPagination(prev => ({ ...prev, page }));
    // Clear selections when changing pages
    setSelectedNotifications(new Set());
    // Scroll to top when changing pages
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleLimitChange = (e) => {
    const newLimit = parseInt(e.target.value, 10);
    setPagination(prev => ({ 
      ...prev, 
      limit: newLimit,
      page: 1
    }));
    // Clear selections when changing limit
    setSelectedNotifications(new Set());
  };

  const unreadNotifications = useMemo(() => {
    return notifications.filter(n => n.status === 'unread');
  }, [notifications]);

  return (
    <PortalPageLayout user={user}>
      <Container className="my-4">
        <Row>
          <Col>
            {/* Header with darker background */}
            <Card className="mb-4 shadow-sm border-0" style={{ backgroundColor: 'rgba(255, 255, 255, 0.85)' }}>
              <Card.Body>
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h2 className="text-dark mb-2">
                      <i className="bi bi-bell me-2 text-primary"></i>
                      My Notifications
                    </h2>
                    <p className="text-muted mb-0">
                      Stay updated with your latest notifications
                      {stats.total && (
                        <span className="ms-2">
                          ({stats.total} total, {stats.unread} unread)
                        </span>
                      )}
                    </p>
                  </div>
                  <Button 
                    variant="outline-secondary" 
                    onClick={() => history.goBack()}
                    className="ms-auto"
                  >
                    <i className="bi bi-arrow-left me-1"></i>
                    Back
                  </Button>
                </div>
              </Card.Body>
            </Card>

            {/* Bulk Actions */}
            {unreadNotifications.length > 0 && (
              <Card className="mb-3 border-0 shadow-sm" style={{ backgroundColor: 'rgba(227, 242, 253, 0.8)' }}>
                <Card.Body className="py-3">
                  <div className="d-flex justify-content-between align-items-center">
                    <div className="d-flex align-items-center gap-3">
                      <Form.Check
                        type="checkbox"
                        id="select-all"
                        label={`Select All (${unreadNotifications.length} unread)`}
                        checked={selectedNotifications.size === unreadNotifications.length && unreadNotifications.length > 0}
                        onChange={handleSelectAll}
                        className="fw-semibold"
                        disabled={bulkLoading}
                      />
                      <small className="text-muted">
                        {selectedNotifications.size} selected
                      </small>
                    </div>
                    
                    {selectedNotifications.size > 0 && (
                      <Button 
                        variant="primary" 
                        size="sm"
                        onClick={handleMarkMultipleAsRead}
                        disabled={bulkLoading}
                      >
                        {bulkLoading ? (
                          <>
                            <i className="bi bi-arrow-repeat me-1" style={{ animation: 'spin 1s linear infinite' }}></i>
                            Processing...
                          </>
                        ) : (
                          <>
                            <i className="bi bi-check-all me-1"></i>
                            Mark as Read ({selectedNotifications.size})
                          </>
                        )}
                      </Button>
                    )}
                  </div>
                </Card.Body>
              </Card>
            )}

            {/* Error Alert */}
            {error && (
              <Alert variant="danger" dismissible onClose={() => setError(null)} className="shadow-sm" style={{ backgroundColor: 'rgba(248, 215, 218, 0.85)' }}>
                <Alert.Heading className="h6">
                  <i className="bi bi-exclamation-triangle me-2"></i>
                  Error
                </Alert.Heading>
                {error}
              </Alert>
            )}

            {/* Loading Spinner */}
            {isLoading && (
              <Card className="text-center py-5 border-0 shadow-sm" style={{ backgroundColor: 'rgba(255, 255, 255, 0.85)' }}>
                <Card.Body>
                  <Spinner animation="border" role="status" variant="primary">
                    <span className="visually-hidden">Loading...</span>
                  </Spinner>
                  <p className="mt-3 text-muted">Loading your notifications...</p>
                </Card.Body>
              </Card>
            )}

            {/* Notifications List */}
            {!isLoading && (
              <>
                {/* Fetching overlay */}
                {isFetching && (
                  <div className="position-relative">
                    <div 
                      className="position-absolute w-100 h-100 d-flex align-items-center justify-content-center"
                      style={{ 
                        backgroundColor: 'rgba(255, 255, 255, 0.8)', 
                        zIndex: 10,
                        borderRadius: '8px'
                      }}
                    >
                      <div className="text-center">
                        <Spinner animation="border" size="sm" variant="primary" />
                        <small className="d-block mt-2 text-muted">Updating...</small>
                      </div>
                    </div>
                  </div>
                )}
                
                {notifications.length === 0 ? (
                  <Card className="text-center py-5 border-0 shadow-sm" style={{ backgroundColor: 'rgba(255, 255, 255, 0.85)' }}>
                    <Card.Body>
                      <i className="bi bi-bell-slash" style={{ fontSize: '4rem', color: '#6c757d' }}></i>
                      <h5 className="mt-3 text-muted">No notifications found</h5>
                      <p className="text-muted">You're all caught up! New notifications will appear here.</p>
                    </Card.Body>
                  </Card>
                ) : (
                  <div className={`notifications-list position-relative ${isFetching ? 'opacity-75' : ''}`}>
                    {notifications.map((notification) => (
                      <Card 
                        key={notification.id}
                        className={`mb-3 shadow-sm ${notification.status === 'unread' ? 'border-danger border-2' : 'border-0'}`}
                        style={{
                          backgroundColor: notification.status === 'unread' ? 'rgba(255, 245, 245, 0.85)' : 'rgba(255, 255, 255, 0.85)'
                        }}
                      >
                        <Card.Body>
                          <div className="d-flex align-items-start gap-3">
                            {notification.status === 'unread' && (
                              <Form.Check
                                type="checkbox"
                                checked={selectedNotifications.has(notification.id)}
                                onChange={(e) => handleSelectNotification(notification.id, e.target.checked)}
                                className="mt-1"
                                disabled={readingActions.has(notification.id) || unreadingActions.has(notification.id)}
                              />
                            )}
                            
                            <div className="flex-grow-1">
                              <p className="mb-2 text-dark">{notification.message}</p>
                              
                              {/* Display metadata if available */}
                              {notification.metadata && notification.metadata.fileNames && (
                                <div className="mb-2">
                                  <small className="text-info">
                                    <i className="bi bi-file-earmark me-1"></i>
                                    File: {notification.metadata.fileNames}
                                  </small>
                                </div>
                              )}
                              
                              <div className="d-flex justify-content-between align-items-center">
                                <div className="d-flex align-items-center gap-3">
                                  <small className="text-muted">
                                    <i className="bi bi-calendar me-1"></i>
                                    {formatDate(notification.created_at)}
                                  </small>
                                  {notification.read_at && (
                                    <small className="text-success">
                                      <i className="bi bi-check-circle me-1"></i>
                                      Read {formatDate(notification.read_at)}
                                    </small>
                                  )}
                                </div>
                                
                                <div className="d-flex gap-2">
                                  {notification.status === 'unread' ? (
                                    <Button
                                      variant="outline-primary"
                                      size="sm"
                                      onClick={() => handleMarkAsRead(notification.id)}
                                      disabled={readingActions.has(notification.id)}
                                    >
                                      {readingActions.has(notification.id) ? (
                                        <>
                                          <i className="bi bi-arrow-repeat me-1" style={{ animation: 'spin 1s linear infinite' }}></i>
                                          Marking as Read...
                                        </>
                                      ) : (
                                        <>
                                          <i className="bi bi-check me-1"></i>
                                          Mark as Read
                                        </>
                                      )}
                                    </Button>
                                  ) : (
                                    <Button
                                      variant="outline-secondary"
                                      size="sm"
                                      onClick={() => handleMarkAsUnread(notification.id)}
                                      disabled={unreadingActions.has(notification.id)}
                                    >
                                      {unreadingActions.has(notification.id) ? (
                                        <>
                                          <i className="bi bi-arrow-repeat me-1" style={{ animation: 'spin 1s linear infinite' }}></i>
                                          Marking as Unread...
                                        </>
                                      ) : (
                                        <>
                                          <i className="bi bi-envelope me-1"></i>
                                          Mark as Unread
                                        </>
                                      )}
                                    </Button>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        </Card.Body>
                      </Card>
                    ))}
                  </div>
                )}

                {/* Pagination */}
                {(notifications.length > 0 || paginationData.totalPages > 0) && (
                  <Card className="mt-4 border-0 shadow-sm" style={{ backgroundColor: 'rgba(255, 255, 255, 0.85)' }}>
                    <Card.Body>
                      <Row className="align-items-center">
                        <Col lg={8}>
                          <CustomPagination
                            page={pagination.page}
                            limit={pagination.limit}
                            total={paginationData.totalCount || 0}
                            onPageChange={handlePageChange}
                            onLimitChange={handleLimitChange}
                            disabled={isLoading || isFetching}
                          />
                        </Col>
                        
                        <Col lg={4} className="text-lg-end text-center mt-3 mt-lg-0">
                          {/* Pagination Statistics */}
                          <div className="d-flex flex-column align-items-lg-end align-items-center">
                            <small className="text-muted mb-1">
                              {isFetching ? (
                                <>
                                  <i className="bi bi-arrow-repeat me-1" style={{ animation: 'spin 1s linear infinite' }}></i>
                                  Loading...
                                </>
                              ) : (
                                <>
                                  Showing {Math.min(((pagination.page - 1) * pagination.limit) + 1, paginationData.totalCount || 0)} to{' '}
                                  {Math.min(pagination.page * pagination.limit, paginationData.totalCount || 0)} of{' '}
                                  {paginationData.totalCount || 0} notifications
                                </>
                              )}
                            </small>
                            
                            {paginationData.totalPages > 1 && (
                              <small className="text-muted">
                                Page {pagination.page} of {paginationData.totalPages}
                                {paginationData.hasNextPage && (
                                  <Button
                                    variant="link"
                                    size="sm"
                                    className="p-0 ms-2 text-decoration-none"
                                    onClick={() => handlePageChange(pagination.page + 1)}
                                    disabled={isLoading || isFetching}
                                  >
                                    Next <i className="bi bi-arrow-right"></i>
                                  </Button>
                                )}
                              </small>
                            )}
                          </div>
                        </Col>
                      </Row>
                      
                      {/* Quick navigation for large datasets */}
                      {paginationData.totalPages > 10 && (
                        <Row className="mt-3">
                          <Col className="text-center">
                            <small className="text-muted me-3">Quick jump:</small>
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              onClick={() => handlePageChange(1)}
                              disabled={pagination.page === 1 || isLoading || isFetching}
                              className="me-2"
                            >
                              <i className="bi bi-chevron-double-left"></i> First
                            </Button>
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              onClick={() => handlePageChange(paginationData.totalPages)}
                              disabled={pagination.page === paginationData.totalPages || isLoading || isFetching}
                            >
                              Last <i className="bi bi-chevron-double-right"></i>
                            </Button>
                          </Col>
                        </Row>
                      )}
                    </Card.Body>
                  </Card>
                )}
              </>
            )}
          </Col>
        </Row>
      </Container>
    </PortalPageLayout>
  );
};

export default UserNotifications; 